import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Checkout, CompanyCarrier, CompanyCarrierTestResult } from 'depoto-core/src/entities'
import { NotificationService } from '../../services'
import { FormControl, FormGroup } from '@angular/forms'
import { SchemaUtil, historyBack } from '../../utils'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
// TitleService removed (unused)
import { CheckoutService } from '../../services/entity-services/checkout.service'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { CompanyCarrierService } from '../../services/entity-services/companyCarrier.service'
import { updateCachedListQueries } from '../../services/query-cache.service'
import { CarrierService } from '../../services/entity-services/carrier.service'
import { GeoConfig, GeoListItemType } from '../../configs'
import { Subscription } from 'rxjs'

type DpdOption = 'newDpd' | 'oldDpd'

const DPD_SETTINGS_KEYS = new Set([
  'customerId',
  'customerAddressId',
  'username',
  'password',
  'apiKey',
  'apiKeyTest',
  'customerDsw',
  'customerAddressIt4emId',
  'notification',
  'insurance',
  'codPayment',
  'dpdRest',
])

const createOldDpdSettings = () => ({
  customerId: '',
  customerAddressId: '',
  username: '',
  password: '',
  dpdRest: false,
})

const createNewDpdSettings = () => ({
  apiKey: '',
  apiKeyTest: '',
  customerDsw: '',
  customerAddressIt4emId: '',
  notification: false,
  insurance: false,
  codPayment: 'Cash',
  dpdRest: true,
})

const createOneSettings = () => ({
  apiKey: '',
  password: '',
  senderCompanyName: '',
  senderFirstName: '',
  senderLastName: '',
  senderStreet: '',
  senderStreetNumber: '',
  senderFlatNumber: '',
  senderZip: '',
  senderCity: '',
  senderCountry: '',
  senderPhone: '',
  senderEmail: '',
})

const createFedexSettings = () => ({
  clientId: '',
  clientSecret: '',
  accountNumber: '',
  clientIdTest: '',
  clientSecretTest: '',
  accountNumberTest: '',
  trackClientId: '',
  trackClientSecret: '',
  trackClientIdTest: '',
  trackClientSecretTest: '',
  termsOfSale: 'DAP',
  eori: '',
  productDesc: '',
})

export const FEDEX_PRODUCT_DESC_MAX_LENGTH = 50

export const companyCarrierSchema = {
  id: null,
  enable: null,
  options: null,
  carrier: SchemaUtil.carrier,
  checkout: {
    id: null,
    name: null,
  },
  externalId: null,
}

@Component({
  selector: 'app-company-carrier',
  templateUrl: 'company-carrier.component.html',
  styleUrls: ['./company-carrier.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
class CompanyCarrierComponent implements CheckDataChanges, OnDestroy {
  private checkoutService = inject(CheckoutService)
  private companyCarrierService = inject(CompanyCarrierService)
  private carrierService = inject(CarrierService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  private initialFormValue = signal<any>(null)
  submitted = signal(false)

  companyCarrierForm = new FormGroup({
    externalId: new FormControl({ value: '', disabled: false }, []),
    name: new FormControl({ value: '', disabled: false }, []),
    carrier: new FormControl({ value: '', disabled: false }, []),
    enable: new FormControl({ value: false, disabled: false }, []),
    checkout: new FormControl({ value: 0, disabled: false }, []),
  })

  carriersWithSettingsObject = [
    'cp',
    'cp_np',
    'cp_nb',
    'cp_bn',
    'dhl_express',
    'dpd',
    'dpd_sk',
    'fedex',
    'gls',
    'one',
    'ppl',
    'zasilkovna',
    'zasilkovna_dpd',
    'zasilkovna_be',
    'zasilkovna_cargus',
    'zasilkovna_cp',
    'zasilkovna_de_hermes_pp',
    'zasilkovna_de_hermes',
    'zasilkovna_dhl',
    'zasilkovna_es_mrw',
    'zasilkovna_es_mrw_hd',
    'zasilkovna_es_mrw_pp',
    'zasilkovna_es_correos',
    'zasilkovna_eo',
    'zasilkovna_fr_colissimo',
    'zasilkovna_cr_hr',
    'zasilkovna_hu_hd',
    'zasilkovna_ie_fastway',
    'zasilkovna_it',
    'zasilkovna_lu',
    'zasilkovna_mp',
    'zasilkovna_sk',
    'zasilkovna_nl_post',
    'zasilkovna_paczkomat',
    'zasilkovna_pl_hd',
    'zasilkovna_plpost',
    'zasilkovna_pt_mrw',
    'zasilkovna_at',
    'zasilkovna_si_post',
    'zasilkovna_sp',
    'zasilkovna_se',
    'zasilkovna_se_pp',
    'zasilkovna_ch',
    'zasilkovna_gb_rm48',
    'zasilkovna_dk',
    'zasilkovna_gr_acs',
    'zasilkovna_gr_elta',
    'ulozenka',
    'shippypro',
    'spring_xbs',
    'wedo',
    'messenger',
    'ups',
    'sps',
    'distribox',
    'geis',
    'toptrans',
    '123kuryr',
    'baselinker',
    'email',
    'fofr',
    'go',
    'intime',
    'shippypro_dhl',
    'shippypro_fedex',
    'shippypro_spring',
    'shippypro_swisspost',
    'ulozenka_cp',
    'ulozenka_dpd',
    'wedo_box',
    'wedo_home',
    'wedo_point',
  ]

  // 'cp' || 'cp_np' || 'cp_nb' || 'cp_bn'
  cpSetting = signal({
    customerID: '',
    contractNumber: '',
    postCode: '',
    locationNumber: '',
    apiToken: '',
    apiSecret: '',
  })

  // dhl_express
  dhlExpressSetting = signal({ username: '', password: '', account_number: '', content_description: '' })

  // dpd
  oldDpdSetting = signal(createOldDpdSettings())
  newDpdSetting = signal(createNewDpdSettings())
  dpdUnknownSettings = signal<Record<string, unknown>>({})
  dpdOptions: DpdOption[] = ['newDpd', 'oldDpd']
  selectedDpdOption = signal<DpdOption>('newDpd')
  dpdCodPaymentOptions = [
    { id: 'Cash', name: 'carrier.codPayment.cash' },
    { id: 'CashOrCard', name: 'carrier.codPayment.cashOrCard' },
  ]

  // one by allegro
  oneSetting = signal(createOneSettings())
  oneSenderExpanded = signal(false)
  oneSenderCountryOptions: GeoListItemType[] = []

  // dpd_sk
  dpdSkSetting = signal({ customerId: '', customerAddressId: '', email: '', apiKey: '' })

  // fedex
  fedexSetting = signal(createFedexSettings())
  fedexTermsOfSaleOptions = ['DAP', 'DDP']
  readonly fedexProductDescMaxLength = FEDEX_PRODUCT_DESC_MAX_LENGTH

  // gls
  glsSetting = signal({ clientNumber: '', username: '', password: '', myGls: false })

  // ppl
  newPplSetting = signal({ username: '', password: '', pplv2: true })

  // ppl
  oldPplSetting = signal({
    username: '',
    password: '',
    customerId: '',
    depo: '',
    numbers: {},
    numbersArr: [],
    pplv2: false,
  })

  /// zasilkovna
  zasilkovnaSetting = signal({ eshop: '', api_password: '' })

  // zasilkovna_dpd
  zasilkovna_dpdSetting = signal({ eshop: '', api_password: '' })

  zasilkovna_beSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_cargusSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_cpSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_de_hermes_ppSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_de_hermesSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_dhlSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_es_mrwSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_es_mrw_hdSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_es_mrw_ppSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_es_correosSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_eoSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_fr_colissimoSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_cr_hrSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_hu_hdSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_ie_fastwaySetting = signal({ eshop: '', api_password: '' })
  zasilkovna_itSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_luSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_mpSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_skSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_nl_postSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_paczkomatSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_pl_hdSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_plpostSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_pt_mrwSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_atSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_si_postSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_spSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_seSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_se_ppSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_chSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_dkSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_gr_acsSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_gr_eltaSetting = signal({ eshop: '', api_password: '' })
  zasilkovna_gb_rm48Setting = signal({ eshop: '', api_password: '' })

  // ulozenka
  ulozenkaSetting = signal({ shopId: '', apiKey: '' })

  // shippypro
  shippyproSetting = signal({
    apikey: '',
    contentDescription: '',
    packageLength: '',
    packageWidth: '',
    packageHeight: '',
    packageWeight: '',
  })

  // spring_xbs
  springXbsSetting = signal({ apikey: '' })

  // wedo
  wedoSetting = signal({ username: '', password: '', customerId: '', department: '' })

  // messenger
  messengerSetting = signal({
    importKey: '',
    username: '',
    password: '',
    contactPerson: '',
  })

  // ups
  upsSetting = signal({ accountNumber: '', username: '', password: '' })

  // sps
  spsSetting = signal({ username: '', password: '' })

  // distribox
  distriboxSetting = signal({ partnerCode: '', bearerToken: '', duration: '' })

  // geis
  geisSetting = signal({ CustomerCode: '', password: '' })

  // toptrans
  toptransSetting = signal({ username: '', password: '' })

  numberskuryrSetting = signal({ username: '', password: '', addressId: '' })

  baselinkerSetting = signal({ shop: '' })

  fofrSetting = signal({ companyId: '', branchCode: '', username: '', accountNumber: '', accountBank: '' })

  intimeSetting = signal({ username: '', password: '', customer: '', department: '' })

  shippypro_dhlSetting = signal({
    apikey: '',
    contentDescription: '',
    packageLength: '',
    packageWidth: '',
    packageHeight: '',
    packageWeight: '',
  })

  shippypro_fedexSetting = signal({
    apikey: '',
    contentDescription: '',
    packageLength: '',
    packageWidth: '',
    packageHeight: '',
    packageWeight: '',
  })

  shippypro_springSetting = signal({
    apikey: '',
    contentDescription: '',
    packageLength: '',
    packageWidth: '',
    packageHeight: '',
    packageWeight: '',
  })

  shippypro_swisspostSetting = signal({
    apikey: '',
    contentDescription: '',
    packageLength: '',
    packageWidth: '',
    packageHeight: '',
    packageWeight: '',
  })

  ulozenka_cpSetting = signal({ apiKey: '', shopId: '' })

  ulozenka_dpdSetting = signal({ apiKey: '', shopId: '' })

  wedo_boxSetting = signal({ username: '', password: '', customer: '', pickupPlaceCode: '' })

  wedo_homeSetting = signal({ username: '', password: '', customer: '', pickupPlaceCode: '' })

  wedo_pointSetting = signal({ username: '', password: '', customer: '', pickupPlaceCode: '' })

  readonly queryCarriersOptions = injectQuery(() => ({
    queryKey: ['carriersOptions'],
    queryFn: async () => {
      return await this.carrierService
        .getCarriers(
          {
            sort: 'name',
            direction: 'asc',
          },
          {
            id: null,
            name: null,
            tariffs: {
              id: null,
              const: null,
              name: null,
              cod: null,
              position: null,
            },
          }
        )
        .then(result => result.items)
    },
  }))

  readonly queryCheckoutsOptions = injectQuery(() => ({
    queryKey: ['checkoutsOptions'],
    queryFn: async () => {
      return await this.checkoutService
        .getCheckouts({
          page: 1,
          sort: 'id',
          direction: 'asc',
        })
        .then(result => {
          // return result.items.map(item => item.id)
          return [{ id: 0, name: ' ' }, ...result.items]
        })
    },
  }))

  pplOptions = ['newPpl', 'oldPpl']
  selectedPplOption = signal('newPpl')

  private languageChangeSubscription: Subscription

  readonly queryCompanyCarrier = injectQuery(() => ({
    queryKey: ['companyCarrier', this.route.snapshot.params['id']],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.companyCarrierService.getCompanyCarrier(id, companyCarrierSchema).then(result => {
          if (this.carriersWithSettingsObject.some(c => c === result.carrier?.id)) {
            this.setSettings(result)
          }
          this.fillFormValue(result)
          return result
        })
      } else {
        this.fillFormValue(new CompanyCarrier())
        return new CompanyCarrier()
      }
    },
    // Always refetch when the component mounts to ensure fresh data on each open
    refetchOnMount: 'always',
  }))

  createCompanyCarrier = injectMutation(() => ({
    mutationFn: async () => {
      const n = this.getDataToSave()

      return await this.companyCarrierService.createCompanyCarrier(n, companyCarrierSchema) // todo add schema
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      this.initialFormValue.set(this.snapshotCurrentForm())
      this.companyCarrierForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.carrier.created'))
      // Update detail cache for this company carrier
      this.queryClient.setQueryData(['companyCarrier', data.id], data)
      // Refresh listing to include the newly created carrier
      this.queryClient.invalidateQueries({ queryKey: ['companyCarriers'] })
      this.router.navigate(['/company-carrier', data['id']], { replaceUrl: true })
    },
  }))

  updateCompanyCarrier = injectMutation(() => ({
    mutationFn: () => {
      const companyCarrierDataToSave = this.getDataToSave()
      return this.companyCarrierService.updateCompanyCarrier(companyCarrierDataToSave, companyCarrierSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      this.initialFormValue.set(this.snapshotCurrentForm())
      this.companyCarrierForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.carrier.updated'))
      // Update detail cache for this customer
      this.queryClient.setQueryData(['companyCarrier', Number(data.id)], data)
      // Update all cached list queries beginning with ['customers', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['companyCarriers'], data)

      // this.removeCustomerListings()

      this.router.navigate(['/company-carrier', data['id']])
    },
  }))

  deleteCompanyCarrier = injectMutation(() => ({
    mutationFn: () => {
      const id = Number(this.route.snapshot.params['id'])
      return this.companyCarrierService.deleteCompanyCarrier({ id })
    },
    onSuccess: () => {
      this.submitted.set(false)
      this.notificationService.success(this.translateService.instant('alert.carrier.deleted'))
      // Prune deleted ompanyCarrier from any cached lists (supports infinite queries and arrays)
      const idStr = String(this.route.snapshot.params['id'])
      updateCachedListQueries(this.queryClient, ['companyCarriers'], { id: Number(idStr) } as any, { mode: 'delete' })
      // Remove detail cache for this companyCarrier
      this.queryClient.removeQueries({ queryKey: ['companyCarrier', Number(idStr)], exact: true })
      this.router.navigate(['/company-carriers'], { replaceUrl: true })
    },
  }))

  testResult = signal<CompanyCarrierTestResult | null>(null)

  testCompanyCarrier = injectMutation(() => ({
    mutationFn: () => this.companyCarrierService.testCompanyCarrier(Number(this.route.snapshot.params['id'])),
    onMutate: () => this.testResult.set(null),
    onSuccess: result => this.testResult.set(result),
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    // return this.queryCompanyCarrier.isFetching() || this.queryCompanyCarrier.isPending()
    return this.queryCompanyCarrier.isFetching() || this.queryCompanyCarrier.isPending()
  })

  tariffOptions = computed(() => {
    return this.queryCarriersOptions.data()?.find(o => o.id === this.companyCarrierForm.value.carrier)?.tariffs
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    this.setOneSenderCountries()
    this.languageChangeSubscription = this.translateService.onLangChange.subscribe(() => this.setOneSenderCountries())

    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different company-carrier ids. This mutates the app-wide Router's routeReuseStrategy though,
    // not something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
    effect(() => {
      this.queryCheckoutsOptions.data()
      this.queryCarriersOptions.data()
      this.queryCompanyCarrier.data()
    })
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
    this.languageChangeSubscription.unsubscribe()
  }

  setSettings(companyCarrier) {
    if (
      companyCarrier.carrier?.id === 'cp' ||
      companyCarrier.carrier?.id === 'cp_np' ||
      companyCarrier.carrier?.id === 'cp_nb' ||
      companyCarrier.carrier?.id === 'cp_bn'
    ) {
      this.cpSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'dhl_express') {
      this.dhlExpressSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'dpd') {
      const options = this.parseOptionsObj(companyCarrier.options)
      this.dpdUnknownSettings.set(this.extractUnknownDpdSettings(options))
      if (options?.dpdRest === true) {
        this.selectedDpdOption.set('newDpd')
        this.newDpdSetting.set(this.normalizeNewDpdSettings(options))
      } else {
        this.selectedDpdOption.set('oldDpd')
        this.oldDpdSetting.set(this.normalizeOldDpdSettings(options))
      }
    } else if (companyCarrier.carrier?.id === 'dpd_sk') {
      this.dpdSkSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'fedex') {
      this.fedexSetting.set(this.normalizeFedexSettings(this.parseOptionsObj(companyCarrier.options)))
    } else if (companyCarrier.carrier?.id === 'gls') {
      this.glsSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'one') {
      this.oneSetting.set(this.normalizeOneSettings(this.parseOptionsObj(companyCarrier.options)))
    } else if (companyCarrier.carrier?.id === 'ppl') {
      if (this.parseOptionsObj(companyCarrier.options)?.pplv2) {
        this.selectedPplOption.set('newPpl')
        this.newPplSetting.set(this.parseOptionsObj(companyCarrier.options))
      } else {
        this.selectedPplOption.set('oldPpl')
        // this.oldPplSetting.set(this.parseOptionsObj(companyCarrier.options))
        this.oldPplSetting.set(this.parseOptionsObj(companyCarrier.options, true))
      }
    } else if (companyCarrier.carrier?.id === 'zasilkovna') {
      this.zasilkovnaSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_dpd') {
      this.zasilkovna_dpdSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_be') {
      this.zasilkovna_beSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_cargus') {
      this.zasilkovna_cargusSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_cp') {
      this.zasilkovna_cpSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_de_hermes_pp') {
      this.zasilkovna_de_hermes_ppSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_de_hermes') {
      this.zasilkovna_de_hermesSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_dhl') {
      this.zasilkovna_dhlSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_es_mrw') {
      this.zasilkovna_es_mrwSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_es_mrw_hd') {
      this.zasilkovna_es_mrw_hdSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_es_mrw_pp') {
      this.zasilkovna_es_mrw_ppSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_es_correos') {
      this.zasilkovna_es_correosSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_eo') {
      this.zasilkovna_eoSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_fr_colissimo') {
      this.zasilkovna_fr_colissimoSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_cr_hr') {
      this.zasilkovna_cr_hrSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_hu_hd') {
      this.zasilkovna_hu_hdSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_ie_fastway') {
      this.zasilkovna_ie_fastwaySetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_it') {
      this.zasilkovna_itSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_lu') {
      this.zasilkovna_luSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_mp') {
      this.zasilkovna_mpSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_sk') {
      this.zasilkovna_skSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_nl_post') {
      this.zasilkovna_nl_postSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_pt_mrw') {
      this.zasilkovna_pt_mrwSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_pl_hd') {
      this.zasilkovna_pl_hdSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_plpost') {
      this.zasilkovna_plpostSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_at') {
      this.zasilkovna_atSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_si_post') {
      this.zasilkovna_si_postSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_sp') {
      this.zasilkovna_spSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_ch') {
      this.zasilkovna_chSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_dk') {
      this.zasilkovna_dkSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_gr_acs') {
      this.zasilkovna_gr_acsSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_gr_elta') {
      this.zasilkovna_gr_eltaSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_gb_rm48') {
      this.zasilkovna_gb_rm48Setting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_se') {
      this.zasilkovna_seSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'zasilkovna_se_pp') {
      this.zasilkovna_se_ppSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'ulozenka') {
      this.ulozenkaSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'shippypro') {
      this.shippyproSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'spring_xbs') {
      this.springXbsSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'wedo') {
      this.wedoSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'messenger') {
      this.messengerSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'ups') {
      this.upsSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'sps') {
      this.spsSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'distribox') {
      this.distriboxSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'geis') {
      this.geisSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'toptrans') {
      this.toptransSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'numberskuryr') {
      this.numberskuryrSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'baselinker') {
      this.baselinkerSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'fofr') {
      this.fofrSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'intime') {
      this.intimeSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'shippypro_dhl') {
      this.shippypro_dhlSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'shippypro_fedex') {
      this.shippypro_fedexSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'shippypro_spring') {
      this.shippypro_springSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'shippypro_swisspost') {
      this.shippypro_swisspostSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'ulozenka_cp') {
      this.ulozenka_cpSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'ulozenka_dpd') {
      this.ulozenka_dpdSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'wedo_box') {
      this.wedo_boxSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'wedo_home') {
      this.wedo_homeSetting.set(this.parseOptionsObj(companyCarrier.options))
    } else if (companyCarrier.carrier?.id === 'wedo_point') {
      this.wedo_pointSetting.set(this.parseOptionsObj(companyCarrier.options))
    }
  }

  getSettings() {
    if (
      this.companyCarrierForm.value.carrier === 'cp' ||
      this.companyCarrierForm.value.carrier === 'cp_np' ||
      this.companyCarrierForm.value.carrier === 'cp_nb' ||
      this.companyCarrierForm.value.carrier === 'cp_bn'
    ) {
      return this.cpSetting() ? JSON.stringify(this.cpSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'dhl_express') {
      return this.dhlExpressSetting() ? JSON.stringify(this.dhlExpressSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'dpd') {
      const activeSettings = this.selectedDpdOption() === 'newDpd' ? this.newDpdSetting() : this.oldDpdSetting()
      return JSON.stringify({ ...this.dpdUnknownSettings(), ...activeSettings })
    } else if (this.companyCarrierForm.value.carrier === 'dpd_sk') {
      return this.dpdSkSetting() ? JSON.stringify(this.dpdSkSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'fedex') {
      return this.fedexSetting() ? JSON.stringify(this.fedexSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'gls') {
      return this.glsSetting() ? JSON.stringify(this.glsSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'one') {
      return JSON.stringify(this.oneSetting())
    } else if (this.companyCarrierForm.value.carrier === 'ppl') {
      // Choose settings object based on explicit selected option, not truthiness
      if (this.selectedPplOption() === 'newPpl') {
        return this.newPplSetting() ? JSON.stringify(this.newPplSetting()) : {}
      } else {
        return this.oldPplSetting() ? JSON.stringify(this.oldPplSetting()) : {}
      }
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna') {
      return this.zasilkovnaSetting() ? JSON.stringify(this.zasilkovnaSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_dpd') {
      return this.zasilkovna_dpdSetting() ? JSON.stringify(this.zasilkovna_dpdSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_be') {
      return this.zasilkovna_beSetting() ? JSON.stringify(this.zasilkovna_beSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_cargus') {
      return this.zasilkovna_cargusSetting() ? JSON.stringify(this.zasilkovna_cargusSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_cp') {
      return this.zasilkovna_cpSetting() ? JSON.stringify(this.zasilkovna_cpSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_de_hermes_pp') {
      return this.zasilkovna_de_hermes_ppSetting() ? JSON.stringify(this.zasilkovna_de_hermes_ppSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_de_hermes') {
      return this.zasilkovna_de_hermesSetting() ? JSON.stringify(this.zasilkovna_de_hermesSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_dhl') {
      return this.zasilkovna_dhlSetting() ? JSON.stringify(this.zasilkovna_dhlSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_es_mrw') {
      return this.zasilkovna_es_mrwSetting() ? JSON.stringify(this.zasilkovna_es_mrwSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_es_mrw_hd') {
      return this.zasilkovna_es_mrw_hdSetting() ? JSON.stringify(this.zasilkovna_es_mrw_hdSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_es_mrw_pp') {
      return this.zasilkovna_es_mrw_ppSetting() ? JSON.stringify(this.zasilkovna_es_mrw_ppSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_es_correos') {
      return this.zasilkovna_es_correosSetting() ? JSON.stringify(this.zasilkovna_es_correosSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_eo') {
      return this.zasilkovna_eoSetting() ? JSON.stringify(this.zasilkovna_eoSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_fr_colissimo') {
      return this.zasilkovna_fr_colissimoSetting() ? JSON.stringify(this.zasilkovna_fr_colissimoSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_cr_hr') {
      return this.zasilkovna_cr_hrSetting() ? JSON.stringify(this.zasilkovna_cr_hrSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_hu_hd') {
      return this.zasilkovna_hu_hdSetting() ? JSON.stringify(this.zasilkovna_hu_hdSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_ie_fastway') {
      return this.zasilkovna_ie_fastwaySetting() ? JSON.stringify(this.zasilkovna_ie_fastwaySetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_it') {
      return this.zasilkovna_itSetting() ? JSON.stringify(this.zasilkovna_itSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_lu') {
      return this.zasilkovna_luSetting() ? JSON.stringify(this.zasilkovna_luSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_mp') {
      return this.zasilkovna_mpSetting() ? JSON.stringify(this.zasilkovna_mpSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_sk') {
      return this.zasilkovna_skSetting() ? JSON.stringify(this.zasilkovna_skSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_nl_post') {
      return this.zasilkovna_nl_postSetting() ? JSON.stringify(this.zasilkovna_nl_postSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_paczkomat') {
      return this.zasilkovna_paczkomatSetting() ? JSON.stringify(this.zasilkovna_paczkomatSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_pl_hd') {
      return this.zasilkovna_pl_hdSetting() ? JSON.stringify(this.zasilkovna_pl_hdSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_plpost') {
      return this.zasilkovna_plpostSetting() ? JSON.stringify(this.zasilkovna_plpostSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_pt_mrw') {
      return this.zasilkovna_pt_mrwSetting() ? JSON.stringify(this.zasilkovna_pt_mrwSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_at') {
      return this.zasilkovna_atSetting() ? JSON.stringify(this.zasilkovna_atSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_si_post') {
      return this.zasilkovna_si_postSetting() ? JSON.stringify(this.zasilkovna_si_postSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_sp') {
      return this.zasilkovna_spSetting() ? JSON.stringify(this.zasilkovna_spSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_ch') {
      return this.zasilkovna_chSetting() ? JSON.stringify(this.zasilkovna_chSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_dk') {
      return this.zasilkovna_dkSetting() ? JSON.stringify(this.zasilkovna_dkSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_gr_acs') {
      return this.zasilkovna_gr_acsSetting() ? JSON.stringify(this.zasilkovna_gr_acsSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_gr_elta') {
      return this.zasilkovna_gr_eltaSetting() ? JSON.stringify(this.zasilkovna_gr_eltaSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_gb_rm48') {
      return this.zasilkovna_gb_rm48Setting() ? JSON.stringify(this.zasilkovna_gb_rm48Setting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_se') {
      return this.zasilkovna_seSetting() ? JSON.stringify(this.zasilkovna_seSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'zasilkovna_se_pp') {
      return this.zasilkovna_se_ppSetting() ? JSON.stringify(this.zasilkovna_se_ppSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'ulozenka') {
      return this.ulozenkaSetting() ? JSON.stringify(this.ulozenkaSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'shippypro') {
      return this.shippyproSetting() ? JSON.stringify(this.shippyproSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'spring_xbs') {
      return this.springXbsSetting() ? JSON.stringify(this.springXbsSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'wedo') {
      return this.wedoSetting() ? JSON.stringify(this.wedoSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'messenger') {
      return this.messengerSetting() ? JSON.stringify(this.messengerSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'ups') {
      return this.upsSetting() ? JSON.stringify(this.upsSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'sps') {
      return this.spsSetting() ? JSON.stringify(this.spsSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'distribox') {
      return this.distriboxSetting() ? JSON.stringify(this.distriboxSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'geis') {
      return this.geisSetting() ? JSON.stringify(this.geisSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'toptrans') {
      return this.toptransSetting() ? JSON.stringify(this.toptransSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'numberskuryr') {
      return this.numberskuryrSetting() ? JSON.stringify(this.numberskuryrSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'baselinker') {
      return this.baselinkerSetting() ? JSON.stringify(this.baselinkerSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'fofr') {
      return this.fofrSetting() ? JSON.stringify(this.fofrSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'intime') {
      return this.intimeSetting() ? JSON.stringify(this.intimeSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'shippypro_dhl') {
      return this.shippypro_dhlSetting() ? JSON.stringify(this.shippypro_dhlSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'shippypro_fedex') {
      return this.shippypro_fedexSetting() ? JSON.stringify(this.shippypro_fedexSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'shippypro_spring') {
      return this.shippypro_springSetting() ? JSON.stringify(this.shippypro_springSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'shippypro_swisspost') {
      return this.shippypro_swisspostSetting() ? JSON.stringify(this.shippypro_swisspostSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'ulozenka_cp') {
      return this.ulozenka_cpSetting() ? JSON.stringify(this.ulozenka_cpSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'ulozenka_dpd') {
      return this.ulozenka_dpdSetting() ? JSON.stringify(this.ulozenka_dpdSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'wedo_box') {
      return this.wedo_boxSetting() ? JSON.stringify(this.wedo_boxSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'wedo_home') {
      return this.wedo_homeSetting() ? JSON.stringify(this.wedo_homeSetting()) : {}
    } else if (this.companyCarrierForm.value.carrier === 'wedo_point') {
      return this.wedo_pointSetting() ? JSON.stringify(this.wedo_pointSetting()) : {}
    }
  }

  selectedPplChanged(option) {
    this.selectedPplOption.set(option)

    // Ensure old PPL always has at least one editable tariff row, without stacking
    // a new blank row every time the select is toggled back to 'oldPpl'.
    if (option === 'oldPpl' && (this.oldPplSetting().numbersArr?.length ?? 0) === 0) {
      this.addPplNumberRow()
    }
  }

  selectedDpdChanged(option: DpdOption): void {
    this.selectedDpdOption.set(option)
  }

  toggleOneSender(): void {
    this.oneSenderExpanded.update(expanded => !expanded)
  }

  submitCarrier(): void {
    this.submitted.set(true)
    if (this.companyCarrierForm.invalid || this.isOptionsObjInvalid()) {
      return
    }

    if (this.isCreating()) {
      this.createCompanyCarrier.mutate()
    } else {
      this.updateCompanyCarrier.mutate()
    }
  }

  confirmDeleteCarrier(): void {
    const confirmation = confirm(
      this.translateService.instant('company-carrier.delete.confirmation', {
        name: this.companyCarrierForm.value.name,
      })
    )
    if (confirmation) {
      this.deleteCompanyCarrier.mutate()
    }
  }

  runCarrierTest(): void {
    // The server tests the saved profile, so unsaved edits would not be part of the test
    if (this.hasUnsavedDataChanges()) {
      this.notificationService.warning(this.translateService.instant('company-carrier.test.unsaved-changes'), '', false)
      return
    }
    this.testCompanyCarrier.mutate()
  }

  goBack(): void {
    historyBack()
  }

  settingsChanged(value, property, settingsObject) {
    settingsObject.set({ ...settingsObject(), [property]: value })
  }

  public fillFormValue(companyCarrier) {
    const value = {
      externalId: companyCarrier?.externalId ?? '',
      enable: companyCarrier?.enable ?? false,
      name: companyCarrier?.name ?? '',
      // store carrier id directly in the form control
      carrier: companyCarrier?.carrier?.id ?? '',
      checkout: companyCarrier?.checkout?.id ?? 0,
    }
    const initialOptions = this.normalizePersistedSettings(
      companyCarrier?.carrier?.id,
      this.safeParse(companyCarrier?.options)
    )

    this.companyCarrierForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone({ ...value, options: initialOptions }))
    this.companyCarrierForm.markAsPristine()
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.companyCarrierForm.getRawValue() as any
    const initial = this.initialFormValue()

    // If initial is not yet set (e.g., brand-new form), fall back to form dirty flag
    if (!initial) {
      return this.companyCarrierForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    const carrierId = current?.carrier
    const settingsChanged =
      (carrierId === 'dpd' || carrierId === 'one') &&
      this.stableSerialize(this.getNormalizedSettingsForSave()) !== this.stableSerialize(initial.options ?? {})

    const changed =
      norm(current?.name) !== norm(initial.name) ||
      norm(current?.enable) !== norm(initial.enable) ||
      norm(current?.carrier) !== norm(initial.carrier) ||
      norm(current?.checkout) !== norm(initial.checkout) ||
      settingsChanged

    return changed
  }

  getDataToSave() {
    const current = this.companyCarrierForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Required fields todo ? if create vs update vsude refactor

    // Optional fields — include only when changed
    const changedDefault = (current?.default ?? false) !== (initial?.default ?? false)
    if (changedDefault) patch.default = current.default

    // Optional fields – include only when changed against initial snapshot
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const idArray = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((x: any) => x?.id).filter((v: any) => v !== undefined && v !== null) : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }
    const eqArrayStrings = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.map(String).slice().sort() : []
      const bb = Array.isArray(b) ? b.map(String).slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    // Optional fields — include only when changed
    if (norm(current.externalId) !== norm(initial?.externalId)) {
      patch.externalId = current.externalId
    }
    if (String(!!current.enable) !== String(!!initial?.enable)) {
      patch.enable = current.enable
    }
    if (norm(current.carrier) !== norm(initial?.carrier)) {
      patch.carrier = { id: current.carrier }
    }
    if (norm(current.checkout) !== norm(initial?.checkout)) {
      patch.checkout = { id: current.checkout }
    }
    // options: build normalized options object and serialize to JSON string only if changed
    const currOptions = this.getNormalizedSettingsForSave()

    const initOptions = initial?.options ?? {}

    if (this.stableSerialize(currOptions) !== this.stableSerialize(initOptions)) {
      patch.options = JSON.stringify(currOptions)
    }

    // +++ check options
    return patch
  }

  updateCarrier(carrierId: string) {
    if (!carrierId) {
      return
    }
    // carrier control now holds carrier id directly
    this.companyCarrierForm.get('carrier')?.setValue(carrierId)
  }

  updateCheckout(checkoutId: number | string): void {
    const ch = this.queryCheckoutsOptions.data().filter(checkout => checkout.id === Number(checkoutId))
    let c = null
    if (ch && ch.length > 0) {
      c = new Checkout(ch[0])
    }
    // Properly update the reactive form control instead of mutating form.value
    this.companyCarrierForm.get('checkout')?.setValue(c)
  }

  parseOptionsObj(options: any, isPpl = false): any {
    if (!options) return {}
    let result: any
    if (typeof options === 'string') {
      try {
        result = JSON.parse(options)
      } catch {
        result = {}
      }
    } else if (typeof options === 'object') {
      result = options
    } else {
      result = {}
    }
    if (isPpl) {
      const nums = result.numbers || {}
      const numsArr: any[] = []
      Object.keys(nums).forEach(k => {
        numsArr.push({ tariffId: k, from: Number(nums[k].from), to: Number(nums[k].to) })
      })
      result.numbersArr = numsArr
    }
    return result
  }

  addPplNumberRow() {
    // if (this.oldPplSetting().numbersArr?.length > 0) {
    //   this.oldPplSetting().numbersArr.push({ tariffId: '', from: '', to: '' }) // todo ok?
    // } else {
    //   this.oldPplSetting.set({ ...this.oldPplSetting(), numbersArr: [{ tariffId: '', from: '', to: '' }] })
    // }
    const current = this.oldPplSetting()
    const numbersArr = Array.isArray(current.numbersArr) ? current.numbersArr : []
    this.oldPplSetting.set({ ...current, numbersArr: [...numbersArr, { tariffId: '', from: '', to: '' }] })
  }

  removePplNumberRow(row: any) {
    // this.oldPplSetting().numbersArr.forEach((r, i) => {
    //   if (row.tariffId === r.tariffId) {
    //     this.oldPplSetting().numbersArr.splice(i, 1)
    //   }
    // })
    const current = this.oldPplSetting()
    const numbersArr = Array.isArray(current.numbersArr) ? current.numbersArr : []
    // Remove by identity, not by tariffId value - multiple rows can share an empty/duplicate tariffId
    this.oldPplSetting.set({ ...current, numbersArr: numbersArr.filter(r => r !== row) })
  }

  isOptionsObjInvalid(): boolean {
    const isEmpty = (v: any) => v === '' || v === null || v === undefined
    const carrierId = this.companyCarrierForm.get('carrier')?.value

    if (carrierId === 'dpd' && this.selectedDpdOption() === 'newDpd') {
      const settings = this.newDpdSetting()
      return [settings.apiKey, settings.customerDsw, settings.customerAddressIt4emId].some(isEmpty)
    }

    if (carrierId === 'one') {
      return isEmpty(this.oneSetting().apiKey) || isEmpty(this.oneSetting().password)
    }

    if (carrierId === 'fedex') {
      const settings = this.fedexSetting()
      return (
        this.isFedexCredentialMissing(settings.clientId) ||
        this.isFedexCredentialMissing(settings.clientSecret) ||
        (this.hasAnyFedexSetting() && !this.isFedexAccountNumberValid(settings.accountNumber)) ||
        !this.isFedexAccountNumberValid(settings.accountNumberTest, true) ||
        !this.isFedexTrackPairValid(settings.trackClientId, settings.trackClientSecret) ||
        !this.isFedexTrackPairValid(settings.trackClientIdTest, settings.trackClientSecretTest) ||
        `${settings.productDesc ?? ''}`.length > FEDEX_PRODUCT_DESC_MAX_LENGTH
      )
    }

    return false
  }

  hasOptionsObjAnyKeys(): boolean {
    return this.carriersWithSettingsObject.some(c => c === this.companyCarrierForm.value.carrier)
  }

  // --- Carrier settings helpers ---
  private setOneSenderCountries(): void {
    this.oneSenderCountryOptions = [
      { name: 'carrier.senderCountry.companyDefault', code: '' },
      ...GeoConfig.getCountries(this.translateService.currentLang),
    ]
  }

  private normalizeOldDpdSettings(source: any): ReturnType<typeof createOldDpdSettings> {
    const s = source && typeof source === 'object' ? source : {}
    return {
      customerId: s.customerId ?? '',
      customerAddressId: s.customerAddressId ?? '',
      username: s.username ?? '',
      password: s.password ?? '',
      dpdRest: false,
    }
  }

  private normalizeNewDpdSettings(source: any): ReturnType<typeof createNewDpdSettings> {
    const s = source && typeof source === 'object' ? source : {}
    return {
      apiKey: s.apiKey ?? '',
      apiKeyTest: s.apiKeyTest ?? '',
      customerDsw: s.customerDsw ?? '',
      customerAddressIt4emId: s.customerAddressIt4emId ?? '',
      notification: s.notification === true,
      insurance: s.insurance === true,
      codPayment: s.codPayment === 'CashOrCard' ? 'CashOrCard' : 'Cash',
      dpdRest: true,
    }
  }

  // The API settings are either left completely empty, or clientId, clientSecret and accountNumber are required.
  // termsOfSale always has a default, so it doesn't count as filled.
  hasAnyFedexSetting(): boolean {
    const { termsOfSale, ...settings } = this.fedexSetting()
    return Object.values(settings).some(value => !!`${value ?? ''}`.trim())
  }

  isFedexCredentialMissing(value: string): boolean {
    return this.hasAnyFedexSetting() && !`${value ?? ''}`.trim()
  }

  isFedexAccountNumberValid(value: string, optional = false): boolean {
    const trimmed = `${value ?? ''}`.trim()
    return (optional && !trimmed) || /^\d{9}$/.test(trimmed)
  }

  // Filled in, but not exactly 9 digits — shown while typing; an empty required accountNumber is only flagged on save
  isFedexAccountNumberFormatInvalid(value: string): boolean {
    return !this.isFedexAccountNumberValid(value, true)
  }

  // Track API credentials are optional, but must be filled in together
  isFedexTrackPairValid(clientId: string, clientSecret: string): boolean {
    return !`${clientId ?? ''}`.trim() === !`${clientSecret ?? ''}`.trim()
  }

  private normalizeFedexSettings(source: any): ReturnType<typeof createFedexSettings> {
    const s = source && typeof source === 'object' ? source : {}
    const defaults = createFedexSettings()
    // Keep only the current REST API keys; the old SOAP credentials (key, password, meter_number) are dropped.
    // account_number and product_desc carry over from the old settings.
    return {
      ...Object.keys(defaults).reduce((settings, key) => ({ ...settings, [key]: s[key] ?? defaults[key] }), defaults),
      accountNumber: s.accountNumber ?? s.account_number ?? '',
      productDesc: s.productDesc ?? s.product_desc ?? '',
    }
  }

  private normalizeOneSettings(source: any): ReturnType<typeof createOneSettings> {
    const s = source && typeof source === 'object' ? source : {}
    const defaults = createOneSettings()
    return Object.keys(defaults).reduce((settings, key) => ({ ...settings, [key]: s[key] ?? defaults[key] }), {
      ...s,
      ...defaults,
    })
  }

  private normalizePersistedSettings(carrierId: string | undefined, source: any): any {
    if (carrierId === 'dpd') {
      const activeSettings =
        source?.dpdRest === true ? this.normalizeNewDpdSettings(source) : this.normalizeOldDpdSettings(source)
      return { ...this.extractUnknownDpdSettings(source), ...activeSettings }
    }
    if (carrierId === 'one') {
      return this.normalizeOneSettings(source)
    }
    if (carrierId === 'fedex') {
      return this.normalizeFedexSettings(source)
    }
    return source
  }

  private extractUnknownDpdSettings(source: any): Record<string, unknown> {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return {}
    }

    return Object.entries(source).reduce<Record<string, unknown>>((unknownSettings, [key, value]) => {
      if (!DPD_SETTINGS_KEYS.has(key)) {
        unknownSettings[key] = value
      }
      return unknownSettings
    }, {})
  }

  private getNormalizedSettingsForSave(): any {
    const settings = this.getSettings()
    let current: any

    if (typeof settings === 'string') {
      current = this.safeParse(settings)
    } else if (settings && typeof settings === 'object') {
      current = structuredClone(settings)
    } else {
      current = {}
    }

    if (
      this.companyCarrierForm.value?.carrier === 'ppl' &&
      (this.selectedPplOption() === 'oldPpl' || current?.pplv2 === false)
    ) {
      const arr = Array.isArray(current?.numbersArr) ? current.numbersArr : []
      const numbers: Record<string, { from: number; to: number }> = {}
      arr
        .filter(row => row && row.tariffId)
        .forEach(row => {
          const from = Number(row.from)
          const to = Number(row.to)
          numbers[String(row.tariffId)] = { from: isNaN(from) ? 0 : from, to: isNaN(to) ? 0 : to }
        })
      current = { ...current, numbers, pplv2: false }
      delete current.numbersArr
    }

    return current
  }

  private snapshotCurrentForm(): any {
    return structuredClone({
      ...this.companyCarrierForm.getRawValue(),
      options: this.getNormalizedSettingsForSave(),
    })
  }

  private stableSerialize(value: any): string {
    const sortObject = (item: any): any => {
      if (Array.isArray(item)) {
        return item.map(sortObject)
      }
      if (item && typeof item === 'object') {
        return Object.keys(item)
          .sort()
          .reduce((sorted, key) => ({ ...sorted, [key]: sortObject(item[key]) }), {})
      }
      return item
    }

    return JSON.stringify(sortObject(value))
  }

  private safeParse(json?: string) {
    try {
      return json ? JSON.parse(json) : {}
    } catch {
      return {}
    }
  }
}

export default CompanyCarrierComponent
